import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { requestRouteDetails } from '../../../api/services/Routes';

interface IUseRouteMapDetails {
  isMapReady: boolean;
  waypointsOnMap: WaypointMarker[];
  completeAnchorTime?: Date;
  defaultRouteDetails?: RouteDetails;
  raWaypointOnMap?: WaypointMarker | null;
}

export const useRouteMapDetails = ({
  waypointsOnMap,
  completeAnchorTime,
  isMapReady,
  defaultRouteDetails,
  raWaypointOnMap,
}: IUseRouteMapDetails) => {
  const [routeDetails, setRouteDetails] = useState<RouteDetails>();
  const { rideId } = useParams() as { rideId: string };

  const handleDirectionsRequest = useCallback(async () => {
    const validWaypointsOnMap = waypointsOnMap.filter((waypoint) => waypoint.location);

    if (raWaypointOnMap && raWaypointOnMap.location?.placeId) {
      if (raWaypointOnMap.type === 'ra_start') {
        validWaypointsOnMap.unshift(raWaypointOnMap);
      } else {
        validWaypointsOnMap.push(raWaypointOnMap);
      }
    }

    if (defaultRouteDetails && !routeDetails) {
      setRouteDetails(defaultRouteDetails);

      return;
    }

    if (routeDetails && validWaypointsOnMap.length <= 1) {
      setRouteDetails({ ...routeDetails, plannedPath: [], legs: [] });

      return;
    }
    if (validWaypointsOnMap.length > 1) {
      const isEditingAndRouteIsDefined = (rideId && routeDetails !== undefined) || !rideId;
      const isPickupRoute = waypointsOnMap[0].anchor;
      const directionsPayload = {
        ...(isEditingAndRouteIsDefined && {
          isPickup: isPickupRoute,
          datetime: completeAnchorTime?.toISOString() || new Date().toISOString(),
          waypoints: validWaypointsOnMap.map(({ latLng, location }, waypointIndex, { length }) => ({
            // the anchor location can be removed, so this doesn't use the anchor information
            anchor: isPickupRoute ? waypointIndex === 0 : waypointIndex === length - 1,
            latLng,
            location,
          })),
          includePolyline: true,
        }),
        ...(!isEditingAndRouteIsDefined && { rideId: rideId }),
      } as RouteDetailsType;

      const response = await requestRouteDetails(directionsPayload);

      if (response?.type === 'success') {
        const plannedPath = window.google.maps.geometry.encoding.decodePath(response.data.routingInfo.plannedRoute);
        const actualPath = response.data.routingInfo.actualRoute
          ? window.google.maps.geometry.encoding.decodePath(response.data.routingInfo.actualRoute)
          : undefined;

        setRouteDetails({
          legs: response.data.legs,
          plannedPath,
          actualPath,
        });
      }
    }
  }, [completeAnchorTime, waypointsOnMap, raWaypointOnMap, defaultRouteDetails, routeDetails, rideId]);

  const directionsRequestRef = useRef(handleDirectionsRequest);

  directionsRequestRef.current = handleDirectionsRequest;

  useEffect(() => {
    if (!isMapReady) {
      return undefined;
    }

    const timeout = setTimeout(() => directionsRequestRef.current(), 600);

    return () => clearTimeout(timeout);
  }, [waypointsOnMap, raWaypointOnMap, completeAnchorTime, isMapReady, rideId]);

  return { routeDetails };
};
